import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    AutocompleteElement,
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    BaseProductInput,
    BrandInput,
    DataGrid,
    DataGridCard,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import { CREATE_PRODUCT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Brand, Product } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';
import { FLAVOR_TYPES } from '@barboxx/types';
import { enumToList } from '@barboxx/utils';

const columns: GridColDef[] = [
    { field: 'id', headerName: 'Id', width: 100 },
    { field: 'name', headerName: 'Name', width: 170, flex: 1 },
    {
        field: 'abvRange',
        headerName: 'ABV Range',
        width: 130,
        maxWidth: 130,
        align: 'right',
        headerAlign: 'right',
    },
];

interface Props {
    brand: Brand;
    refetch: () => void;
}

export const ProductsCard = ({ brand, refetch }: Props) => {
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<Product.CreateRequest>({
        resolver: yupResolver(CREATE_PRODUCT_SCHEMA) as any,
        defaultValues: {
            brandId: brand.id,
            isAlcoholic: true,
            flavor: null,
        },
    });

    const mutateCreateProduct = useMutation({
        mutationKey: ['product'],
        mutationFn: (data: Product.CreateRequest) =>
            Api.Products.Products.postProduct(data),
    });

    const onSubmit = async (values: Product.CreateRequest) => {
        return mutateCreateProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${data?.name} product!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        navigate(`/products/${params.id}`);
    };

    const values = useWatch(formContext);

    return (
        <>
            <DataGridCard
                title="Products List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Product
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={brand.products}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Product
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <BrandInput disabled />

                        <TextFieldElement label="Name" name="name" fullWidth />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                        />

                        <TextFieldElement
                            label="Volume (ml)"
                            name="volumeMl"
                            fullWidth
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 10000,
                                },
                            }}
                        />

                        <CheckboxElement
                            label="Is Alcoholic"
                            name="isAlcoholic"
                        />

                        {values.isAlcoholic && (
                            <>
                                <TextFieldElement
                                    label="ABV Percentage"
                                    name="abvPercentage"
                                    fullWidth
                                    type="number"
                                    slotProps={{
                                        htmlInput: {
                                            min: 0,
                                            max: 100,
                                        },
                                    }}
                                />
                                <ProductTypeInput />
                                {values.productTypeId && (
                                    <ProductSubtypeInput />
                                )}
                            </>
                        )}

                        <AutocompleteElement
                            label="Flavor"
                            name="flavor"
                            options={enumToList(FLAVOR_TYPES)}
                        />

                        <BaseProductInput />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
